import type { Observable } from 'rxjs/Observable';

import type { DocNode } from '@atlaskit/adf-schema';
import { fetchJson$ } from '@atlassian/jira-fetch/src/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { SwagError } from '@atlassian/jira-software-swag/src/common/utils/error/index.tsx';
import { getCardDescriptionAsADF } from '../../common/utils.tsx';
import type { BulkIssueCreationGraphqlResponse } from './types.tsx';

const MUTATION_NAME = 'BacklogBulkCreateCardWithDescription';

const getConcurrentJiraMutation = (issueNum: number) => {
	const variables = Array.from({ length: issueNum })
		.map((_, i) => `$cardCreateInput${i}: JiraIssueCreateInput!`)
		.join(', ');

	const mutationBodies = Array.from({ length: issueNum })
		.map(
			(_, i) => `
		  request${i}: jira {
			createIssue(input: $cardCreateInput${i}) @optIn(to: "JiraIssueCreateMutation") {
			  success
			  errors {
				message
				extensions {
				  statusCode
				  errorType
				}
			  }
			  issue {
				id
				issueId
				key
				summary
				issueTypeField {
				  issueType {
					issueTypeId
					name
					avatar {
					  medium
					}
					hierarchy {
					  level
					}
				  }
				}
				assigneeField {
				  user {
					id
					accountId
					name
					picture
				  }
				}
				statusField {
				  status {
					statusId
					name
					description
				  }
				}
				statusCategory {
				  statusCategoryId
				  key
				  colorName
				}
				priorityField {
				  priority {
					id
					iconUrl
					name
				  }
				}
				parentIssueField {
				  parentIssue {
					issueId
					key
					summary
					issueTypeField {
					  id
					  name
					  issueType {
						issueTypeId
						name
						id
						avatar {
						  medium
						}
					  }
					}
				  }
				}
				fieldSetsById(fieldSetIds: ["labels"]) {
				  edges {
					node {
					  fields {
						edges {
						  node {
							... on JiraLabelsField {
							  labels {
								edges {
								  node {
									name
								  }
								}
							  }
							}
						  }
						}
					  }
					}
				  }
				}
			  }
			}
		  }
		`,
		)
		.join('');

	return `
	  mutation ${MUTATION_NAME}(${variables}) {
		${mutationBodies}
	  }
	`;
};

type MutationProps = {
	projectId: string;
	cloudId: string;
	issueTypeId: string;
	parentId: string | null;
	fixVersions: string[] | null;
	assigneeId: string | null;
	labels: string[] | null;
	cards: Array<{
		summary: string;
		description: DocNode | string;
	}>;
};

/**
 * This request creates the issues with AGG endpoint, supporting batch creation and additional fields (e.g. description)
 * - The request is replacement for `card-create-multiple-gql`(TMP) and `inline-create-issue` + `fetch-issue`(CMP)
 * 	The response needs to be transformed by `transformBulkCreationResponseToCMPIssues` and `transformBulkCreationResponseToTMPCardCreateGql` first
 * - Manually throw error when errors present in the response; Throw `SwagError` to tap into existing logics (SwagError should be renamed to `GqlError` but that's a large scale thing)
 */
export const bulkCreateIssuesWithDescription = ({
	projectId,
	cloudId,
	issueTypeId,
	parentId,
	fixVersions,
	assigneeId,
	labels,
	cards,
}: MutationProps): Observable<BulkIssueCreationGraphqlResponse> => {
	const variables: Record<string, object> = {};
	const projectAri = createAri({
		resourceOwner: 'jira',
		cloudId,
		resourceType: 'project',
		resourceId: projectId,
	});
	const issueTypeAri = createAri({
		resourceOwner: 'jira',
		cloudId,
		resourceType: 'issue-type',
		resourceId: issueTypeId,
	});

	cards.forEach((card, i) => {
		variables[`cardCreateInput${i}`] = {
			projectId: projectAri,
			issueTypeId: issueTypeAri,
			fields: {
				singleLineTextFields: [{ fieldId: 'summary', text: card.summary }],
				richTextFields: [
					{
						fieldId: 'description',
						richText: {
							adfValue: getCardDescriptionAsADF(card.description),
						},
					},
				],
				...(parentId && {
					parentField: {
						issueId: createAri({
							resourceOwner: 'jira',
							cloudId,
							resourceType: 'issue',
							resourceId: parentId,
						}),
					},
				}),
				...(fixVersions?.length && {
					multipleVersionPickerFields: [
						{
							fieldId: 'fixVersions',
							versions: fixVersions.map((versionId) => ({ versionId })),
						},
					],
				}),
				...(assigneeId && {
					singleSelectUserPickerFields: [
						{
							fieldId: 'assignee',
							user: {
								accountId: assigneeId,
							},
						},
					],
				}),
				...(labels?.length && {
					labelsFields: [
						{
							fieldId: 'labels',
							labels: labels.map((name) => ({ name })),
						},
					],
				}),
			},
		};
	});

	return fetchJson$<{ data: BulkIssueCreationGraphqlResponse }>('/gateway/api/graphql', {
		method: 'POST',
		body: JSON.stringify({
			operationName: MUTATION_NAME,
			query: getConcurrentJiraMutation(cards.length),
			variables,
		}),
	})
		.map((response) => response.data)
		.map((response) => {
			const errors = Object.values(response).find((request) => request.createIssue?.errors)
				?.createIssue?.errors;

			if (errors?.length) {
				// Just take the first error for the time being
				// Format the error as a swag error to reuse existing checks, down the line we should rename related to `gqlError`
				throw new SwagError({
					graphQLErrors: errors.map(({ message, extensions }) => ({
						message,
						path: [],
						extensions: {
							statusCode: extensions?.statusCode ?? null,
							errorType: extensions?.errorType ?? '',
							userMessage: message,
						},
					})),
					errorMessage: errors[0].message,
				});
			}

			return response;
		});
};
